import { useEffect, useState } from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import {
  getWorkplaceReservations,
  type WorkplaceReservations,
  type WorkplaceReservationsFailure,
  type WorkplaceReservationsRange,
} from '../services/floorplan-editor.service';

export interface WorkplaceReservationsState {
  readonly loading: boolean;
  readonly data?: WorkplaceReservations;
  readonly error?: WorkplaceReservationsFailure;
}

/**
 * Who has the selected desk, read only while the dialog asking is open.
 *
 * Deliberately not tied to the selection: clicking through a plan changes the selected desk
 * constantly, and firing a request that names people every time an admin taps a marker would
 * be both wasteful and a wider disclosure than anyone asked for.
 */
export const useWorkplaceReservations = (
  workplaceId: string | undefined,
  enabled: boolean,
  range?: WorkplaceReservationsRange,
): WorkplaceReservationsState => {
  const keycloak = useKeycloak();
  const [data, setData] = useState<WorkplaceReservations | undefined>();
  const [error, setError] = useState<WorkplaceReservationsFailure | undefined>();
  const [loading, setLoading] = useState(false);
  const from = range?.from;
  const to = range?.to;

  useEffect(() => {
    if (!enabled || !workplaceId) {
      // Cleared on close so reopening on another desk cannot show the previous desk's people
      // for the moment before its own answer arrives.
      setData(undefined);
      setError(undefined);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(undefined);

    getWorkplaceReservations(workplaceId, keycloak, { from, to })
      .then((result) => {
        if (!active) return;

        if (result.status !== 'ok') {
          setData(undefined);
          setError(result.status);
          return;
        }

        setData(result.data);
        setError(undefined);
      })
      .catch(() => {
        if (!active) return;

        setData(undefined);
        setError('failed');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // Depends on the two strings rather than on the range object: a caller rebuilding
    // `{ from, to }` on every render would otherwise refetch the same days forever.
  }, [workplaceId, enabled, keycloak, from, to]);

  return { loading, data, error };
};

export default useWorkplaceReservations;
